import { apiClient } from "./client";
import type {
    ClientHistoryResponse,
    ClientLoyaltyCardEventsResponse,
    ClientLoyaltyCardUpdateResponse,
    ClientPurchasedItemsResponse,
    ClientsDatasetResponse,
} from "./types";

export async function fetchClientsDatasetPage(params?: {
    page?: number;
    perPage?: number;
    search?: string;
}) {
    const response = await apiClient.get<ClientsDatasetResponse>("/clients", {
        params: {
            page: params?.page ?? 1,
            per_page: params?.perPage ?? 25,
            search: params?.search ?? "",
        },
    });

    return response.data;
}

export async function fetchClientsInboxDataset(params?: {
    page?: number;
    perPage?: number;
    search?: string;
}) {
    const response = await apiClient.get<Pick<ClientsDatasetResponse, "loyaltyRows" | "orderRows" | "issues">>(
        "/clients/inbox",
        {
            params: {
                page: params?.page ?? 1,
                per_page: params?.perPage ?? 25,
                search: params?.search ?? "",
            },
        },
    );

    return response.data;
}

/** @deprecated Prefer {@link fetchClientProfile} or {@link fetchClientsDatasetPage}. */
export async function fetchClientsDataset() {
    return fetchClientsDatasetPage({ page: 1, perPage: 50 });
}

export async function fetchClientProfile(profileId: string) {
    const response = await apiClient.get<ClientsDatasetResponse>("/clients/profile", {
        params: {
            profile_id: profileId,
        },
    });

    return response.data;
}

export async function fetchClientProfileRelated(profileId: string) {
    const response = await apiClient.get<{ profiles: ClientsDatasetResponse["profiles"]; lastUpdatedAt: string }>(
        "/clients/profile/related",
        {
            params: {
                profile_id: profileId,
            },
        },
    );

    return response.data;
}

export async function fetchClientProfileCdp(profileId: string) {
    const response = await apiClient.get<{ cdpProfile: NonNullable<ClientsDatasetResponse["cdpProfile"]>; lastUpdatedAt: string }>(
        "/clients/profile/cdp",
        {
            params: {
                profile_id: profileId,
            },
        },
    );

    return response.data;
}

export async function fetchClientPurchasedItems(params: {
    profileId: string;
    dateFrom?: string;
    dateTo?: string;
    articleCode?: string;
    articleName?: string;
    category?: string;
    page?: number;
    perPage?: number;
    sortDirection?: "asc" | "desc";
}) {
    const response = await apiClient.get<ClientPurchasedItemsResponse>("/clients/purchases", {
        params: {
            profile_id: params.profileId,
            ...(params.dateFrom ? { date_from: params.dateFrom } : {}),
            ...(params.dateTo ? { date_to: params.dateTo } : {}),
            ...(params.articleCode ? { article_code: params.articleCode } : {}),
            ...(params.articleName ? { article_name: params.articleName } : {}),
            ...(params.category ? { category: params.category } : {}),
            page: params.page ?? 1,
            per_page: params.perPage ?? 25,
            sort_direction: params.sortDirection ?? "desc",
        },
    });

    return response.data;
}

export async function fetchClientHistory(
    profileId: string,
    customer?: string,
    options?: { light?: boolean; ordersOnly?: boolean },
) {
    const response = await apiClient.get<ClientHistoryResponse>("/clients/history", {
        params: {
            profile_id: profileId,
            ...(customer ? { customer } : {}),
            ...(options?.light ? { light: 1 } : {}),
            ...(options?.ordersOnly ? { orders_only: 1 } : {}),
        },
    });

    return response.data;
}

export async function fetchClientHistoryExtras(profileId: string, customer?: string) {
    const response = await apiClient.get<
        Pick<ClientHistoryResponse, "traderDefect" | "transportHistory" | "transportError" | "expoClientObjects" | "manualProfileLinks" | "lastUpdatedAt"> & {
            canManageProfileLinks?: boolean;
        }
    >("/clients/history/extras", {
        params: {
            profile_id: profileId,
            ...(customer ? { customer } : {}),
        },
    });

    return response.data;
}

export async function fetchClientLoyaltyCardEvents(profileId: string, limit = 30) {
    const response = await apiClient.get<ClientLoyaltyCardEventsResponse>("/clients/loyalty-card/events", {
        params: {
            profile_id: profileId,
            limit,
        },
    });

    return response.data;
}

export async function updateClientLoyaltyCard(payload: {
    profileId: string;
    cardNumber?: string;
    phone: string;
    email?: string;
    holder: string;
    district?: string;
    discount?: string;
    issueCard?: boolean;
    gdprProcessingConsent: boolean;
    gdprMarketingConsent?: boolean;
}) {
    const response = await apiClient.patch<ClientLoyaltyCardUpdateResponse>("/clients/loyalty-card", {
        profile_id: payload.profileId,
        card_number: payload.cardNumber ?? "",
        phone: payload.phone,
        email: payload.email ?? "",
        holder: payload.holder,
        district: payload.district ?? "",
        discount: payload.discount ?? "",
        issue_card: payload.issueCard ?? false,
        gdpr_processing_consent: payload.gdprProcessingConsent,
        gdpr_marketing_consent: payload.gdprMarketingConsent ?? false,
    });

    return response.data;
}

export async function createClientProfileLink(payload: {
    profileId: string;
    relatedProfileId: string;
    note?: string;
}) {
    const response = await apiClient.post<{ success: boolean }>("/clients/profile-links", {
        profile_id: payload.profileId,
        related_profile_id: payload.relatedProfileId,
        ...(payload.note ? { note: payload.note } : {}),
    });

    return response.data;
}

export async function deleteClientProfileLink(payload: {
    profileId: string;
    relatedProfileId: string;
}) {
    const response = await apiClient.delete<{ success: boolean }>("/clients/profile-links", {
        data: {
            profile_id: payload.profileId,
            related_profile_id: payload.relatedProfileId,
        },
    });

    return response.data;
}

export async function approvePartnerMergeSuggestion(payload: { profileId: string; suggestionId: number }) {
    const response = await apiClient.post<{ success: boolean }>("/clients/partner-merge/approve", null, {
        params: {
            profile_id: payload.profileId,
            suggestion_id: payload.suggestionId,
        },
    });

    return response.data;
}

export async function fetchClientSalesActivity(profileId: string) {
    const response = await apiClient.get<import("./types").ClientSalesActivityResponse>(
        "/clients/sales-activity",
        { params: { profile_id: profileId } },
    );

    return response.data;
}

export async function createClientSalesNote(payload: {
    profileId: string;
    body: string;
    followUpAt?: string | null;
}) {
    const response = await apiClient.post<{ note: import("./types").ClientSalesNote }>(
        "/clients/sales-activity/notes",
        {
            profile_id: payload.profileId,
            body: payload.body,
            follow_up_at: payload.followUpAt ?? null,
        },
    );

    return response.data.note;
}

export async function createClientSalesTask(payload: {
    profileId: string;
    title: string;
    dueAt?: string | null;
}) {
    const response = await apiClient.post<{ task: import("./types").ClientSalesTask }>(
        "/clients/sales-activity/tasks",
        {
            profile_id: payload.profileId,
            title: payload.title,
            due_at: payload.dueAt ?? null,
        },
    );

    return response.data.task;
}

export async function completeClientSalesTask(payload: { profileId: string; taskId: number }) {
    const response = await apiClient.patch<{ success: boolean }>(
        "/clients/sales-activity/tasks/complete",
        {
            profile_id: payload.profileId,
            task_id: payload.taskId,
        },
    );

    return response.data;
}

export async function updateClientSalesStatus(payload: { profileId: string; statusCode: string }) {
    const response = await apiClient.put<{ salesStatus: import("./types").ClientSalesStatus }>(
        "/clients/sales-activity/status",
        {
            profile_id: payload.profileId,
            status_code: payload.statusCode,
        },
    );

    return response.data.salesStatus;
}

export async function rejectPartnerMergeSuggestion(payload: { profileId: string; suggestionId: number }) {
    const response = await apiClient.post<{ success: boolean }>("/clients/partner-merge/reject", null, {
        params: {
            profile_id: payload.profileId,
            suggestion_id: payload.suggestionId,
        },
    });

    return response.data;
}
